import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import SearchIcon from '@mui/icons-material/Search';
import { makeStyles } from '@mui/styles';

import { translate } from '@vestahealthcare/common/i18n';
import {
  CareTeamCandidate,
  CommunityResource,
} from '@vestahealthcare/common/models';
import { isValidPhone } from '@vestahealthcare/common/utils/format';
import { formatPhone } from '@vestahealthcare/common/utils/phone';

import { Button, Fonts, PhoneInput, TextInput } from 'styleguide-v2';

import { showGlobalError } from 'dash/src/components/GlobalMessage';
import { fetchCommunityResources } from 'dash/src/services/CommunityResourcesServices';

import { SearchTable } from '../../components/SearchTable';

type Props = {
  disabledCRs?: CommunityResource[];
  onSubmit: (cr: CommunityResource) => void;
  selectedCareTeamCandidate?: CareTeamCandidate;
};

const useStyles = makeStyles({
  container: {},
  searchButton: {
    alignSelf: 'end',
    height: 'fit-content',
    marginBottom: '0.25rem',
    width: '9rem',
  },
  addButon: {
    height: 'fit-content',
    width: 'fit-content',
  },
  separator: {
    margin: 'auto',
    width: '99%',
  },
  subtitle: {
    '&&': {
      fontFamily: Fonts.fontFamily,
      fontSize: Fonts.fontSize,
      fontWeight: 500,
    },
  },
});

export const SearchCommunityReourceStep = ({
  disabledCRs,
  onSubmit,
  selectedCareTeamCandidate,
}: Props) => {
  const styles = useStyles();

  const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
  const [searched, setSearched] = useState<boolean>(false);
  const [crs, setCRs] = useState<CommunityResource[]>();

  const [name, setName] = useState<string | undefined>(
    selectedCareTeamCandidate?.fullName,
  );
  const [phone, setPhone] = useState<string | undefined>(
    selectedCareTeamCandidate?.phone?.number,
  );
  const [searchText, setSearchText] = useState<string | undefined>();

  const handleSearch = async () => {
    if (!name && !phone) return;
    setLoadingSearch(true);

    try {
      const allResults: CommunityResource[] = [];
      const searchQueries: Promise<CommunityResource[]>[] = [];

      if (name && phone) {
        searchQueries.push(
          fetchCommunityResources({
            name,
            phone,
            limit: 25,
          }).then(({ items }) => items),
        );
      }

      if (phone) {
        searchQueries.push(
          fetchCommunityResources({
            phone,
            limit: 25,
          }).then(({ items }) => items),
        );
      }

      if (name) {
        searchQueries.push(
          fetchCommunityResources({
            name,
            limit: 25,
          }).then(({ items }) => items),
        );
      }

      const searchResults = await Promise.all(searchQueries);

      searchResults.forEach((results) => {
        allResults.push(...results);
      });

      const uniqueResults = allResults.filter(
        (cr, index, self) =>
          index === self.findIndex((item) => item.id === cr.id),
      );

      setSearchText(name || formatPhone(phone || ''));
      setCRs(uniqueResults);
    } catch (e) {
      showGlobalError(e as string);
    }

    setLoadingSearch(false);
    setSearched(true);
  };

  const createNewCR = () => {
    onSubmit(
      new CommunityResource({
        name: selectedCareTeamCandidate?.fullName || name,
        interventions: [],
        specialities: [],
        phone: selectedCareTeamCandidate?.phone?.number || phone,
      }),
    );
  };

  useEffect(() => {
    setSearchText(undefined);
  }, []);

  useEffect(() => {
    if (
      selectedCareTeamCandidate &&
      (selectedCareTeamCandidate.fullName ||
        selectedCareTeamCandidate.phone?.number)
    ) {
      handleSearch();
    }
  }, [selectedCareTeamCandidate]);

  return (
    <div className={classNames(styles.container, 'grid-wrapper fit')}>
      <span className="grid-span-12">
        {selectedCareTeamCandidate
          ? translate('careTeam.actions.createCommunityResource')
          : translate('communityResources.steps.searchCommunityResource')}
      </span>
      <TextInput
        className="grid-span-5"
        data-cy="cr-steps-name"
        label={translate('communityResources.modal.name')}
        onChange={(val) => setName(val?.trim())}
        value={name}
      />
      <PhoneInput
        className="grid-span-5"
        data-cy="cr-steps-phone"
        label={translate('communityResources.modal.phoneNumber')}
        onChange={(val) => setPhone(val?.trim())}
        value={phone}
      />
      <Button
        className={classNames(styles.searchButton, 'grid-span-2')}
        color="secondary"
        data-cy="cr-steps-search"
        disabled={!name && (!phone || !isValidPhone(phone))}
        loading={loadingSearch}
        icon={<SearchIcon />}
        onClick={handleSearch}
      >
        {translate('global.search')}
      </Button>

      {searched && (
        <>
          <br />
          <hr className={classNames(styles.separator, 'grid-span-12')} />
          <br />
          {crs?.length ? (
            <div className="grid-span-12">
              <p>
                {translate('communityResources.steps.showingResultsFor')}
                <b>{` "${searchText}"`}</b>:
              </p>
              <SearchTable
                disabled={disabledCRs?.map(({ id }) => id)}
                crs={crs}
                onSelect={onSubmit}
              />
              <br />
              <h4 className={styles.subtitle}>
                {translate('communityResources.steps.notAMatch')}
              </h4>
              <p>
                {translate(
                  'communityResources.steps.noCommunityResourcesFoundBody',
                )}
              </p>
              <br />
              <Button
                className={classNames(styles.addButon, 'grid-span-12')}
                color="secondary"
                type="outlined"
                onClick={createNewCR}
              >
                {translate(
                  'communityResources.steps.noCommunityResourcesFoundButton',
                )}
              </Button>
            </div>
          ) : (
            <div className="grid-span-12">
              <h4 className={styles.subtitle}>
                {translate(
                  'communityResources.steps.noCommunityResourcesFoundTitle',
                )}
              </h4>
              <p>
                {translate(
                  'communityResources.steps.noCommunityResourcesFoundBody',
                )}
              </p>
              <br />
              <Button
                className={classNames(styles.addButon, 'grid-span-12')}
                color="secondary"
                type="outlined"
                onClick={createNewCR}
              >
                {translate(
                  'communityResources.steps.noCommunityResourcesFoundButton',
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
